import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import TableRow from "@mui/material/TableRow";
import TableHead from "@mui/material/TableHead";
import TableBody from "@mui/material/TableBody";
import ModalBasicLayout from "../ModalBasicLayout";
import TableContainer from "../../Tables/TableContainer";
import TableCell from "../../../pages/BillingAndPayments/components/TableCell";
import { useTranslationApp } from "../../../lib/i18next";
import { useSelectorApp, useDispatchApp } from "../../../lib/redux";
import ContainerModalActions from "../../Containers/ContainerModalActions";
import SelectIntegration from "./SelectIntegration";
import {
  setShowModalIntegrationNotFound,
  resetIntegrationNotFound,
} from "../../../actions/alerts";
import {
  startGetCampaignPlatformDataForIntegrationNotFoundModal,
  startUpdateCampaignsPlatformIntegrationFromModal,
} from "../../../actions/adsCampaigns";
import { LaunchError } from "../../../utils/errors";
import { logoPlatform } from "../../../utils/string";
import {
  MODAL_INTEGRATION_NOT_FOUND_TITLE,
  MODAL_INTEGRATION_NOT_FOUND_DESCRIPTION,
  MODAL_INTEGRATION_NOT_FOUND_COLUMN_PLATFORM,
  MODAL_INTEGRATION_NOT_FOUND_COLUMN_CAMPAIGN,
  MODAL_INTEGRATION_NOT_FOUND_COLUMN_AD_ACCOUNT,
  MODAL_INTEGRATION_NOT_FOUND_COLUMN_REASON,
  MODAL_INTEGRATION_NOT_FOUND_COLUMN_INTEGRATION,
  MODAL_INTEGRATION_NOT_FOUND_ROW_DISABLED,
  MODAL_INTEGRATION_NOT_FOUND_ROW_LOADING,
  MODAL_INTEGRATION_NOT_FOUND_ROW_ERROR,
  MODAL_INTEGRATION_NOT_FOUND_SAVE,
  BUTTON_CANCEL,
  READY,
  NAME,
} from "../../../i18n/keysTranslations";
import { getPlatformFromIntegrationKey } from "../../../actions/getters";
import {
  ALERT_ICON_TYPE_SUCCESS,
  FACEBOOK,
  META,
  TIKTOK,
  TIKTOK_BUSINESS,
  GOOGLE,
  LINKEDIN,
  CANVA,
  YOUTUBE,
} from "../../../utils/constants";
import { SimpleAlert } from "../../Alerts/Alerts";

const getIntegrationFieldName = (index) => `integrationNotFound_${index}`;
const getIntegrationsKey = (platform) => {
  if (platform === META) return FACEBOOK;
  if (platform === TIKTOK) return TIKTOK_BUSINESS;
  return platform;
};
const ADVERTISING_PLATFORMS = new Set([META, TIKTOK, GOOGLE, LINKEDIN]);
const ADDITIONAL_PLATFORMS = new Set([CANVA, YOUTUBE]);

const TABLE_CELL_NAME_OR_REASON_SX = { maxWidth: 280, minWidth: 0 };

/** Body cells: allow wrapping; shared TableCell defaults to nowrap + fixed height. */
const TABLE_CELL_NAME_OR_REASON_BODY_SX = {
  ...TABLE_CELL_NAME_OR_REASON_SX,
  whiteSpace: "normal",
  wordBreak: "break-word",
  height: "auto",
  verticalAlign: "middle",
};

/** Campaign & ad account columns: same wrapping, vertically centered in the row. */
const TABLE_CELL_CAMPAIGN_AD_ACCOUNT_BODY_SX = {
  ...TABLE_CELL_NAME_OR_REASON_BODY_SX,
  verticalAlign: "middle",
};

const TYPOGRAPHY_TWO_LINE_CLAMP_SX = {
  ...TABLE_CELL_NAME_OR_REASON_SX,
  display: "-webkit-box",
  WebkitLineClamp: 4,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  wordBreak: "break-word",
};

const buildInitialRowState = (item, urlCampaignID) => {
  const campaignID = item.campaignID || urlCampaignID || null;
  const platform = getPlatformFromIntegrationKey(item.platform);
  const isAdditionalPlatform = ADDITIONAL_PLATFORMS.has(platform);

  return {
    campaignID,
    platform,
    campaignName: "",
    message: item?.message ?? "",
    currentIntegrationID: item.integrationID || "",
    adAccountID: "",
    adAccountName: "",
    // Advertising rows load campaign/ad-account context; additional platforms only need the payload (integration).
    loading: !isAdditionalPlatform && Boolean(campaignID),
    error: false,
    // Additional integrations are not tied to a campaign in this modal; advertising rows need campaignID.
    disabled: !isAdditionalPlatform && !campaignID,
  };
};

const AdvertisingIntegrationsTable = React.memo(
  function AdvertisingIntegrationsTable({ rows = [], control, errors }) {
    const { t } = useTranslationApp();

    return (
      <TableContainer
        hasPagination={false}
        loading={false}
        showEmptyText={false}
        keys={rows.map((_, i) => `advertising-${i}`)}
        page={0}
        onPageChange={() => {}}
        rowsPerPage={Math.max(rows.length, 1)}
        ariaLabel={t(MODAL_INTEGRATION_NOT_FOUND_TITLE)}
        sx={{ borderRadius: "0px !important" }}
        sxContainer={{ boxShadow: "none" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: "16px !important" }} isHeader={true}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_PLATFORM)}
            </TableCell>
            <TableCell isHeader={true} sx={TABLE_CELL_NAME_OR_REASON_SX}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_CAMPAIGN)}
            </TableCell>
            <TableCell isHeader={true} sx={TABLE_CELL_NAME_OR_REASON_SX}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_AD_ACCOUNT)}
            </TableCell>
            <TableCell isHeader={true} sx={TABLE_CELL_NAME_OR_REASON_SX}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_REASON)}
            </TableCell>
            <TableCell isHeader={true}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_INTEGRATION)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ row, index }, rowIndex) => (
            <IntegrationRow
              key={`${row.platform}-${row.campaignID ?? index}`}
              row={row}
              index={index}
              isLastRow={rowIndex === rows.length - 1}
              control={control}
              errors={errors}
              currentIntegrationID={row.currentIntegrationID}
              showAdAccountColumn={true}
            />
          ))}
        </TableBody>
      </TableContainer>
    );
  }
);

const AdditionalIntegrationsTable = React.memo(
  function AdditionalIntegrationsTable({ rows = [], control, errors }) {
    const { t } = useTranslationApp();

    return (
      <TableContainer
        hasPagination={false}
        loading={false}
        showEmptyText={false}
        keys={rows.map((_, i) => `additional-${i}`)}
        page={0}
        onPageChange={() => {}}
        rowsPerPage={Math.max(rows.length, 1)}
        ariaLabel={t(MODAL_INTEGRATION_NOT_FOUND_TITLE)}
        sx={{ borderRadius: "0px !important" }}
        sxContainer={{ boxShadow: "none" }}
      >
        <TableHead>
          <TableRow>
            <TableCell sx={{ pl: "16px !important" }} isHeader={true}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_PLATFORM)}
            </TableCell>
            <TableCell isHeader={true} sx={TABLE_CELL_NAME_OR_REASON_SX}>
              {t(NAME)}
            </TableCell>
            <TableCell isHeader={true} sx={TABLE_CELL_NAME_OR_REASON_SX}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_REASON)}
            </TableCell>
            <TableCell isHeader={true}>
              {t(MODAL_INTEGRATION_NOT_FOUND_COLUMN_INTEGRATION)}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map(({ row, index }, rowIndex) => (
            <IntegrationRow
              key={`${row.platform}-${row.campaignID ?? index}`}
              row={row}
              index={index}
              isLastRow={rowIndex === rows.length - 1}
              control={control}
              errors={errors}
              currentIntegrationID={row.currentIntegrationID}
              showAdAccountColumn={false}
            />
          ))}
        </TableBody>
      </TableContainer>
    );
  }
);

const ModalIntegrationNotFound = React.memo(
  function ModalIntegrationNotFound() {
    const { t } = useTranslationApp();
    const dispatch = useDispatchApp();
    const [searchParams] = useSearchParams();

    const modalOpen = useSelectorApp(
      (s) => s?.alerts?.integrationNotFound?.open
    );
    const alertItems = useSelectorApp(
      (s) => s?.alerts?.integrationNotFound?.items || []
    );

    const [rows, setRows] = useState([]);
    const [isSaving, setIsSaving] = useState(false);

    const {
      control,
      reset,
      watch,
      getValues,
      formState: { errors },
    } = useForm({ defaultValues: {} });

    const urlCampaignID = searchParams.get("campaignID");

    const integrationFieldNames = useMemo(
      () => rows.map((_, i) => getIntegrationFieldName(i)),
      [rows]
    );

    const watchedIntegrations = watch(integrationFieldNames);

    const onCloseModal = () => {
      dispatch(setShowModalIntegrationNotFound(false));
      dispatch(resetIntegrationNotFound());
    };

    useEffect(() => {
      if (!modalOpen) {
        setRows([]);
        return;
      }
      if (alertItems.length === 0) return;
      if (rows.length > 0) return;
      const nextRows = alertItems.map((item) =>
        buildInitialRowState(item, urlCampaignID)
      );
      setRows(nextRows);
      const defaults = {};
      nextRows.forEach((row, i) => {
        defaults[getIntegrationFieldName(i)] = "";
      });
      reset(defaults);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [modalOpen, alertItems]);

    useEffect(() => {
      if (!modalOpen || rows.length === 0) return;

      rows.forEach((row, index) => {
        if (!row.loading || row.disabled) return;

        const fetchData = async () => {
          try {
            const result = await dispatch(
              startGetCampaignPlatformDataForIntegrationNotFoundModal({
                campaignID: row.campaignID,
                platform: row.platform,
              })
            );

            if (!result.ok) {
              setRows((prev) =>
                prev.map((r, i) =>
                  i === index ? { ...r, loading: false, error: true } : r
                )
              );
              return;
            }

            setRows((prev) =>
              prev.map((r, i) =>
                i === index
                  ? {
                      ...r,
                      campaignName: result.name,
                      currentIntegrationID: result.integrationID,
                      adAccountID: result.adAccountID,
                      adAccountName: result.adAccountName ?? "",
                      loading: false,
                      error: false,
                    }
                  : r
              )
            );
          } catch {
            setRows((prev) =>
              prev.map((r, i) =>
                i === index ? { ...r, loading: false, error: true } : r
              )
            );
          }
        };

        fetchData();
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows.length, modalOpen]);

    const hasRowsLoading = rows.some((r) => r.loading);
    const actionableRowsExist = rows.some((r) => !r.disabled && !r.error);
    const allActionableSelected = rows.every((row, index) => {
      if (row.disabled || row.error) return true;
      return Boolean(watchedIntegrations?.[index]);
    });
    const canSave =
      !hasRowsLoading &&
      !isSaving &&
      actionableRowsExist &&
      allActionableSelected;

    const onSave = async () => {
      setIsSaving(true);
      try {
        const values = getValues();
        const integrationSelections = rows.map(
          (_, index) => values[getIntegrationFieldName(index)]
        );
        const { ok } = await dispatch(
          startUpdateCampaignsPlatformIntegrationFromModal({
            rows,
            integrationSelections,
          })
        );
        if (ok) {
          SimpleAlert({
            title: t(READY),
            icon: ALERT_ICON_TYPE_SUCCESS,
          });
          onCloseModal();
        }
      } catch (error) {
        dispatch(LaunchError(error));
      } finally {
        setIsSaving(false);
      }
    };

    const hasValidRows = rows.length > 0;
    const advertisingRows = useMemo(
      () =>
        rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => ADVERTISING_PLATFORMS.has(row.platform)),
      [rows]
    );
    const additionalRows = useMemo(
      () =>
        rows
          .map((row, index) => ({ row, index }))
          .filter(({ row }) => ADDITIONAL_PLATFORMS.has(row.platform)),
      [rows]
    );

    const shouldShowAdvertisingTable = advertisingRows.length > 0;
    const shouldShowAdditionalTable = additionalRows.length > 0;

    return (
      <ModalBasicLayout
        modalOpen={modalOpen && hasValidRows}
        onCloseModal={onCloseModal}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        title={t(MODAL_INTEGRATION_NOT_FOUND_TITLE)}
        description={t(MODAL_INTEGRATION_NOT_FOUND_DESCRIPTION)}
        sxDescription={{ whiteSpace: "pre-line" }}
      >
        <Box sx={{ width: "100%", mt: 1 }}>
          {shouldShowAdvertisingTable ? (
            <Box>
              <AdvertisingIntegrationsTable
                rows={advertisingRows}
                control={control}
                errors={errors}
              />
            </Box>
          ) : null}

          {shouldShowAdditionalTable ? (
            <Box sx={{ mt: shouldShowAdvertisingTable ? 2 : 0 }}>
              <AdditionalIntegrationsTable
                rows={additionalRows}
                control={control}
                errors={errors}
              />
            </Box>
          ) : null}
        </Box>

        <DialogActions sx={{ px: 0, pt: 2, pb: 0 }}>
          <ContainerModalActions
            hasPadding={true}
            propsSecondaryButton={{
              showButton: true,
              onClick: onCloseModal,
              text: BUTTON_CANCEL,
            }}
            propsPrimaryButton={{
              showButton: true,
              onClick: onSave,
              text: MODAL_INTEGRATION_NOT_FOUND_SAVE,
              type: "button",
              loading: isSaving,
              disabled: !canSave,
            }}
          />
        </DialogActions>
      </ModalBasicLayout>
    );
  }
);

const IntegrationRow = React.memo(function IntegrationRow({
  row,
  index,
  isLastRow,
  control,
  errors,
  currentIntegrationID,
  showAdAccountColumn = true,
}) {
  const { t } = useTranslationApp();
  const integrations = useSelectorApp((state) => state?.user?.integrations);
  const platformLogoSrc = logoPlatform(row.platform);
  const fieldName = getIntegrationFieldName(index);

  const renderSelector = () => {
    if (row.disabled) {
      return (
        <Typography variant="body2" color="text.secondary">
          {t(MODAL_INTEGRATION_NOT_FOUND_ROW_DISABLED)}
        </Typography>
      );
    }
    if (row.loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t(MODAL_INTEGRATION_NOT_FOUND_ROW_LOADING)}
          </Typography>
        </Box>
      );
    }
    if (row.error) {
      return (
        <Typography variant="body2" color="error">
          {t(MODAL_INTEGRATION_NOT_FOUND_ROW_ERROR)}
        </Typography>
      );
    }
    return (
      <SelectIntegration
        control={control}
        name={fieldName}
        platform={row.platform}
        onlyShowWithThisAdAccountID={row.adAccountID || undefined}
        errors={errors}
        currentIntegrationID={currentIntegrationID}
      />
    );
  };

  const integrationName = useMemo(() => {
    if (!row.currentIntegrationID) return "";
    const integrationID = String(row.currentIntegrationID)?.trim();
    if (!integrationID) return "";

    const platformKey = getIntegrationsKey(row.platform);
    const platformIntegrations = integrations?.[platformKey];
    const integration = platformIntegrations?.[integrationID];

    return integration?.name || integrationID;
  }, [integrations, row.currentIntegrationID, row.platform]);

  const campaignLabel = showAdAccountColumn
    ? row.campaignName || row.campaignID || "—"
    : integrationName || row.currentIntegrationID || "—";
  const adAccountNameTrimmed = row.adAccountName?.trim() || "";
  const adAccountPrimaryLabel = adAccountNameTrimmed || row.adAccountID || "—";
  const adAccountSecondaryId =
    adAccountNameTrimmed && row.adAccountID ? row.adAccountID : "";
  const reasonText =
    row.message != null && String(row.message).trim() !== ""
      ? String(row.message)
      : "-";

  return (
    <TableRow>
      <TableCell
        indexRow={index}
        isLastRow={isLastRow}
        sx={{ pl: "16px !important", cursor: "default" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {platformLogoSrc ? (
            <Avatar
              src={platformLogoSrc}
              variant="square"
              sx={{
                width: 22,
                height: 22,
                "& .MuiAvatar-img": { objectFit: "contain" },
              }}
            />
          ) : null}
          <Typography variant="body2" sx={{ textTransform: "capitalize" }}>
            {row.platform}
          </Typography>
        </Box>
      </TableCell>
      <TableCell
        indexRow={index}
        isLastRow={isLastRow}
        sx={{ cursor: "default", ...TABLE_CELL_CAMPAIGN_AD_ACCOUNT_BODY_SX }}
      >
        {row.loading ? (
          <CircularProgress size={14} />
        ) : (
          <Typography
            variant="body2"
            title={campaignLabel !== "—" ? String(campaignLabel) : undefined}
            sx={TYPOGRAPHY_TWO_LINE_CLAMP_SX}
          >
            {campaignLabel}
          </Typography>
        )}
      </TableCell>
      {showAdAccountColumn ? (
        <TableCell
          indexRow={index}
          isLastRow={isLastRow}
          sx={{ cursor: "default", ...TABLE_CELL_CAMPAIGN_AD_ACCOUNT_BODY_SX }}
        >
          {row.loading ? (
            <CircularProgress size={14} />
          ) : row.error || row.disabled ? (
            <Typography variant="body2" color="text.secondary">
              —
            </Typography>
          ) : (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="body2"
                title={
                  adAccountPrimaryLabel !== "—"
                    ? String(adAccountPrimaryLabel)
                    : undefined
                }
                sx={{
                  ...TABLE_CELL_NAME_OR_REASON_SX,
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {adAccountPrimaryLabel}
              </Typography>
              {adAccountSecondaryId ? (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  component="div"
                  sx={{
                    mt: 0.25,
                    wordBreak: "break-all",
                    lineHeight: 1.2,
                  }}
                >
                  {adAccountSecondaryId}
                </Typography>
              ) : null}
            </Box>
          )}
        </TableCell>
      ) : null}
      <TableCell
        indexRow={index}
        isLastRow={isLastRow}
        sx={{ cursor: "default", ...TABLE_CELL_NAME_OR_REASON_BODY_SX }}
      >
        <Typography
          variant="body2"
          title={reasonText !== "-" ? reasonText : undefined}
          sx={TYPOGRAPHY_TWO_LINE_CLAMP_SX}
        >
          {reasonText}
        </Typography>
      </TableCell>
      <TableCell
        indexRow={index}
        isLastRow={isLastRow}
        sx={{ cursor: "default" }}
      >
        {renderSelector()}
      </TableCell>
    </TableRow>
  );
});

export default ModalIntegrationNotFound;
